// A game session: 10 games drawn from a level's pool of 20, points with a
// streak bonus, stars, and the progress kept in this browser (levels
// unlocked, best scores, how each game is going).
import { LEVELS, type GameDef, type Level, type Rng } from "./types";

export const SESSION_SIZE = 10;
/** Right answers out of 10 to pass a level (and unlock the next). */
export const PASS_SCORE = 8;
/** At most this many signal games in one session: their data is slow to load. */
export const MAX_SIGNAL_GAMES = 3;
/** Signal games start from this position, so their data has time to load. */
export const FIRST_SIGNAL_SLOT = 3;

export const BASE_POINTS = 10;
/** Each right answer in a row adds this much more, up to STREAK_CAP. */
export const STREAK_STEP = 2;
export const STREAK_CAP = 10;

export const LEVEL_META: Record<Level, { label: string; blurb: string }> = {
  beginner: { label: "Beginner", blurb: "Read candles and trends, and do the risk math every trade needs." },
  intermediate: { label: "Intermediate", blurb: "The desk's setups: 4h bias, pullback zone, triggers, stops and targets." },
  expert: { label: "Expert", blurb: "Manage open trades, follow the day's limits, and judge decisions, not luck." },
};

/** Points for one answer: 10 when right, plus 2 for each right answer before it in a row (up to +10). */
export function pointsFor(correct: boolean, streakBefore: number): number {
  return correct ? BASE_POINTS + Math.min(STREAK_CAP, STREAK_STEP * streakBefore) : 0;
}

/** 8 right = 1 star, 9 = 2, 10 = 3. */
export function starsFor(score: number): 0 | 1 | 2 | 3 {
  return score >= 10 ? 3 : score >= 9 ? 2 : score >= PASS_SCORE ? 1 : 0;
}

export interface GameStat {
  played: number;
  correct: number;
}

export interface LevelStat {
  sessions: number;
  best: number;
  passed: boolean;
}

export interface Progress {
  xp: number;
  bestStreak: number;
  levels: Record<Level, LevelStat>;
  games: Record<string, GameStat>;
  /** UTC days ("2026-10-08") with a finished session. */
  days: string[];
}

export function emptyProgress(): Progress {
  return {
    xp: 0,
    bestStreak: 0,
    levels: { beginner: emptyLevel(), intermediate: emptyLevel(), expert: emptyLevel() },
    games: {},
    days: [],
  };
}

const emptyLevel = (): LevelStat => ({ sessions: 0, best: 0, passed: false });

/** Beginner is open; each next level opens once the one before it is passed. */
export function isUnlocked(level: Level, p: Progress): boolean {
  const i = LEVELS.indexOf(level);
  return i <= 0 || p.levels[LEVELS[i - 1]!].passed;
}

/**
 * Draw `n` games from a pool: weighted toward games played less and missed
 * more (so practice goes where it's needed), signal games capped at
 * MAX_SIGNAL_GAMES and placed from FIRST_SIGNAL_SLOT on, the rest shuffled.
 * `signalsOk` false leaves signal games out.
 */
export function pickGames(pool: GameDef[], n: number, rng: Rng, stats: Record<string, GameStat> = {}, signalsOk = true): GameDef[] {
  const weight = (g: GameDef) => {
    const s = stats[g.id];
    if (!s || s.played === 0) return 3;
    const missRate = 1 - s.correct / s.played;
    return 1 + 2 * missRate + 1 / (1 + s.played);
  };
  const left = pool.filter(g => signalsOk || !g.signals);
  const chosen: GameDef[] = [];
  let signalCount = 0;
  while (chosen.length < n && left.length > 0) {
    const allowed = left.filter(g => !g.signals || signalCount < MAX_SIGNAL_GAMES);
    if (allowed.length === 0) break;
    const total = allowed.reduce((s, g) => s + weight(g), 0);
    let x = rng.next() * total;
    let pick = allowed[allowed.length - 1]!;
    for (const g of allowed) {
      x -= weight(g);
      if (x <= 0) { pick = g; break; }
    }
    chosen.push(pick);
    if (pick.signals) signalCount++;
    left.splice(left.indexOf(pick), 1);
  }
  return orderSession(rng.shuffle(chosen));
}

/** Signal games moved to slot FIRST_SIGNAL_SLOT or later (their data loads while the first games are played). */
export function orderSession(games: GameDef[]): GameDef[] {
  const out = games.slice();
  for (let i = 0; i < Math.min(FIRST_SIGNAL_SLOT, out.length); i++) {
    if (!out[i]!.signals) continue;
    const j = out.findIndex((g, k) => k >= FIRST_SIGNAL_SLOT && !g.signals);
    if (j < 0) break;
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out;
}

export interface Result {
  gameId: string;
  correct: boolean;
  points: number;
}

/** UTC day of a timestamp: "2026-10-08". */
export const utcDay = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

/** Progress after a finished session (pure). */
export function applySession(p: Progress, level: Level, results: Result[], nowMs: number): Progress {
  const score = results.filter(r => r.correct).length;
  let streak = 0;
  let best = p.bestStreak;
  for (const r of results) {
    streak = r.correct ? streak + 1 : 0;
    best = Math.max(best, streak);
  }
  const games = { ...p.games };
  for (const r of results) {
    const g = games[r.gameId] ?? { played: 0, correct: 0 };
    games[r.gameId] = { played: g.played + 1, correct: g.correct + (r.correct ? 1 : 0) };
  }
  const lv = p.levels[level];
  const day = utcDay(nowMs);
  return {
    xp: p.xp + results.reduce((s, r) => s + r.points, 0) + (score >= PASS_SCORE ? 50 : 0),
    bestStreak: best,
    levels: { ...p.levels, [level]: { sessions: lv.sessions + 1, best: Math.max(lv.best, score), passed: lv.passed || score >= PASS_SCORE } },
    games,
    days: p.days.includes(day) ? p.days : [...p.days, day].slice(-400),
  };
}

/** Days in a row with a finished session, counting back from today (or yesterday, if today has none yet). */
export function dayStreak(days: string[], nowMs: number): number {
  const set = new Set(days);
  let d = nowMs;
  if (!set.has(utcDay(d))) d -= 86_400_000;
  let n = 0;
  while (set.has(utcDay(d))) {
    n++;
    d -= 86_400_000;
  }
  return n;
}

export const RANKS = [
  { xp: 0, title: "Rookie" },
  { xp: 300, title: "Chart reader" },
  { xp: 1000, title: "Planner" },
  { xp: 2500, title: "Risk manager" },
  { xp: 5000, title: "Disciplined trader" },
  { xp: 10000, title: "Desk pro" },
] as const;

export function rankFor(xp: number): { title: string; next: { title: string; xp: number } | null; progress: number } {
  let i = 0;
  while (i + 1 < RANKS.length && xp >= RANKS[i + 1]!.xp) i++;
  const cur = RANKS[i]!;
  const next = RANKS[i + 1] ?? null;
  return {
    title: cur.title,
    next: next ? { title: next.title, xp: next.xp } : null,
    progress: next ? (xp - cur.xp) / (next.xp - cur.xp) : 1,
  };
}

// ── Storage (this browser) ──────────────────────────────────────────────────

const KEY = "vp.games.v1";
export const PROGRESS_EVENT = "vp:games-progress";

export function readProgress(): Progress {
  const base = emptyProgress();
  try {
    const v = JSON.parse(localStorage.getItem(KEY) ?? "null");
    if (!v || typeof v !== "object") return base;
    return {
      xp: Number.isFinite(v.xp) ? v.xp : 0,
      bestStreak: Number.isFinite(v.bestStreak) ? v.bestStreak : 0,
      levels: {
        beginner: { ...base.levels.beginner, ...v.levels?.beginner },
        intermediate: { ...base.levels.intermediate, ...v.levels?.intermediate },
        expert: { ...base.levels.expert, ...v.levels?.expert },
      },
      games: v.games && typeof v.games === "object" ? v.games : {},
      days: Array.isArray(v.days) ? v.days.filter((d: unknown) => typeof d === "string") : [],
    };
  } catch {
    return base;
  }
}

export function saveProgress(p: Progress): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(p));
  } catch {
    /* storage blocked: progress lasts this visit */
  }
  if (typeof window !== "undefined") window.dispatchEvent(new Event(PROGRESS_EVENT));
}
